import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import DashboardService from "../../services/dashboard.service";
import { BlacklistItem } from "../../types/dashboard";

export const BlacklistConfig = () => {
  const queryClient = useQueryClient();

  const [selectedClientId, setSelectedClientId] = useState("");

  const { data: options, isLoading: loadingOptions } = useQuery(
    ["budget-dashboard-options"],
    () => DashboardService.getBudgetDashboardOptions(),
    { refetchOnWindowFocus: false }
  );

  const {
    data: blacklist = [],
    isLoading: loadingBlacklist,
    refetch: refetchBlacklist,
  } = useQuery<BlacklistItem[]>(
    ["budget-dashboard-blacklist"],
    () => DashboardService.getBlacklist(),
    { refetchOnWindowFocus: false }
  );

  const addMutation = useMutation(
    (clientId: string) => DashboardService.addToBlacklist(clientId),
    {
      onSuccess: () => {
        toast.success("Cliente adicionado à blacklist");
        queryClient.invalidateQueries([
          "budget-dashboard-options",
          "budget-dashboard-project-list",
          "budget-dashboard-summary",
          "budget-dashboard-monthly",
          "budget-dashboard-blacklist",
        ]);
        refetchBlacklist();
        setSelectedClientId("");
      },
      onError: () => {
        toast.error("Erro ao adicionar cliente à blacklist");
      },
    }
  );

  const removeMutation = useMutation(
    (clientId: string) => DashboardService.removeFromBlacklist(clientId),
    {
      onSuccess: () => {
        toast.success("Cliente removido da blacklist");
        queryClient.invalidateQueries([
          "budget-dashboard-options",
          "budget-dashboard-project-list",
          "budget-dashboard-summary",
          "budget-dashboard-monthly",
          "budget-dashboard-blacklist",
        ]);
        refetchBlacklist();
      },
      onError: () => {
        toast.error("Erro ao remover cliente da blacklist");
      },
    }
  );

  const handleAdd = useCallback(() => {
    if (!selectedClientId) return;
    addMutation.mutate(selectedClientId);
  }, [selectedClientId, addMutation]);

  const handleRemove = useCallback(
    (clientId: string) => {
      removeMutation.mutate(clientId);
    },
    [removeMutation]
  );

  const blacklistedClientIds = new Set(blacklist.map((item) => item.clientId));

  const availableClients = (options?.clients || []).filter(
    (client) => !blacklistedClientIds.has(client.id)
  );

  return (
    <Box
      sx={{
        backgroundColor: "#F9FCFF",
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          py: 3,
          px: 3,
        }}
      >
        <Box mb={3}>
          <Typography variant="h5" fontWeight={700}>
            Blacklist de Clientes
          </Typography>
          <Typography color="#64748B">
            Gerencie os clientes que não devem ser contabilizados no Dashboard de
            Orçamentos.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Adicionar cliente
              </Typography>
              <Typography color="#64748B" mb={2} fontSize={14}>
                Selecione um cliente para adicionar à blacklist. Os dados desse
                cliente deixarão de ser considerados no dashboard.
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Cliente</InputLabel>
                <Select
                  label="Cliente"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  disabled={loadingOptions || addMutation.isLoading}
                >
                  <MenuItem value="">
                    <em>Selecione um cliente</em>
                  </MenuItem>
                  {availableClients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.taxId
                        ? `${client.name} - ${client.taxId}`
                        : client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={!selectedClientId || addMutation.isLoading}
                fullWidth
              >
                {addMutation.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Adicionar à Blacklist"
                )}
              </Button>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Clientes na Blacklist
              </Typography>
              <Typography color="#64748B" mb={2} fontSize={14}>
                Estes clientes estão sendo desconsiderados nos indicadores do
                dashboard.
              </Typography>

              {loadingBlacklist ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : blacklist.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: "center",
                    color: "#94A3B8",
                    border: "1px dashed #CBD5E1",
                    borderRadius: 1,
                  }}
                >
                  <Typography>
                    Nenhum cliente na blacklist.
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={1}>
                  {blacklist.map((item) => {
                    const displayName = item.nickname || item.clientName || item.clientId;
                    const taxId = item.taxId || "";

                    return (
                      <Box
                        key={item._id}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          border: "1px solid #E2E8F0",
                          backgroundColor: "#F8FAFC",
                        }}
                      >
                        <Box>
                          <Typography fontWeight={600} fontSize={14}>
                            {displayName}
                          </Typography>
                          <Typography color="#64748B" fontSize={13}>
                            {taxId || "CNPJ/CPF não informado"}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleRemove(item.clientId)}
                          disabled={removeMutation.isLoading}
                        >
                          Remover
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
